/**
 * Client-side API utilities
 */

import { API_ENDPOINTS } from '@/constants';
import type { BlogResponse, BlogFilters } from '@/types/blog';

/**
 * Generic API fetch utility with error handling and type safety
 * Automatically adds Origin header for client-side requests
 */
export async function fetchApi<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  try {
    // Get the current origin (for client-side requests)
    const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000';
    
    const response = await fetch(endpoint, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Origin': origin,
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`API fetch error for ${endpoint}:`, error);
    throw error;
  }
}

/**
 * Build query string from filters object
 */
function buildQueryString(filters: Record<string, any>): string {
  const params = new URLSearchParams();
  
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  });
  
  return params.toString();
}

/**
 * Fetch events data
 */
export async function fetchEvents(skipPastEvents: boolean = true): Promise<any> {
  const backendUrl = process.env.NEXT_PUBLIC_API_BACKEND || 'http://localhost:8000';
  const endpoint = `${backendUrl}/api/v1/events?skipPastEvents=${skipPastEvents}`;
    
  console.log('Fetching events from:', endpoint);
  
  return fetchApi(endpoint);
}

/**
 * Fetch site data by key
 */
export async function fetchSiteDataByKey(key: string): Promise<any> {
  const backendUrl = process.env.NEXT_PUBLIC_API_BACKEND || 'http://localhost:8000';
  const endpoint = `${backendUrl}/api/v1/site-data/key/${key}`;
    
  console.log('Fetching site data by key:', endpoint);
  
  return fetchApi(endpoint);
}

/**
 * Fetch site data with dynamic filters
 */
export async function fetchSiteDataFilters(filters: BlogFilters = {}): Promise<BlogResponse> {
  const backendUrl = process.env.NEXT_PUBLIC_API_BACKEND || 'http://localhost:8000';
  const queryString = buildQueryString(filters);
  const endpoint = queryString 
    ? `${backendUrl}/api/v1/site-data/filters?${queryString}`
    : `${backendUrl}/api/v1/site-data/filters`;
    
  console.log('Fetching site data from:', endpoint);
  console.log('Filters:', filters);
  
  return fetchApi<BlogResponse>(endpoint);
}

/**
 * Specific utility for fetching header data
 */
export async function fetchHeaderData() {
  const backendUrl = process.env.NEXT_PUBLIC_API_BACKEND || 'http://localhost:8000';
  const endpoint = `${backendUrl}/api/v1/site-data/key/header`;
  
  console.log('🔍 [Client] Fetching header from:', endpoint);
  
  const result = await fetchApi(endpoint);
  
  // Return the data property if it exists (backend format)
  if (result.success && result.data) {
    console.log('✅ [Client] Header data received:', result.data);
    return result.data;
  }
  
  // Otherwise return as-is (fallback format)
  return result;
}